import API_BASE_URL from '../config/api';
import React, { useState, useEffect } from 'react';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import { loadGoogleFonts } from '../utils/fonts';
import AppHeader from '../components/AppHeader';
import MobileDrawer from '../components/MobileDrawer';
import coloredLogo from '../images/colored_logo.PNG';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Tabs,
  Tab,
  Paper,
  Chip,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Stack,
  Avatar,
  Alert,
  Fade,
  Grow,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Language,
  Menu,
  Home,
  Store,
  Calculate,
  Info,
  ContactMail,
  WbSunny,
  DarkMode,
  Refresh,
  GetApp,
  Assessment,
  CardGiftcard,
  School,
  Phone,
  AccessTime,
  ShowChart,
  Update,
  AutoGraph
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { useRef } from 'react';
import AppFooter from '../components/AppFooter';
import googleLogo from '../images/googleplay.png'; // Import the logo
import appleLogo from '../images/appstore.png'; // Import the logo
import logo from '../images/colored_logo.PNG'; // Import the logo
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

// ─── Buying / Selling deductions per karat (KD) ───────────────────────────
const KARAT_DEDUCTIONS = {
  '24k': 2.500,
  '24K': 2.500,
  '22k': 3.000,
  '22K': 3.000,
  '21k': 3.000,
  '21K': 3.000,
  '18k': 3.500,
  '18K': 3.500,
};

// ─── MetalsBuySellTable ──────────────────────────────────────────────────────
const MetalsBuySellTable = ({ metalRates, formatPrice, t }) => {
  const PRIMARY = 'black';
  const SECONDARY = '#905A01';
  const SELL_COLOR = '#A3F22F';   // both sell and buy shown in green
  const BUY_COLOR  = '#A3F22F';

  const karats = [
    { label: '24K', sellKey: '24k', altKey: '24K', type: 'gold' },
    { label: '22K', sellKey: '22k', altKey: '22K', type: 'gold' },
    { label: '21K', sellKey: '21k', altKey: '21K', type: 'gold' },
    { label: '18K', sellKey: '18k', altKey: '18K', type: 'gold' },
  ];

  const getSellPrice = (sellKey, altKey, type) => {
    if (type === 'silver') return metalRates?.silver ?? null;
    return metalRates?.gold?.[sellKey] ?? metalRates?.gold?.[altKey] ?? null;
  };

  const getBuyPrice = (sellKey, altKey, type) => {
    const sell = getSellPrice(sellKey, altKey, type);
    if (sell === null) return null;
    if (type === 'silver') return null; // no defined spread for silver
    const deduction = KARAT_DEDUCTIONS[sellKey] ?? KARAT_DEDUCTIONS[altKey] ?? 0;
    return Math.max(0, sell - deduction);
  };

  return (
    <Box
      sx={{
        width: '100%',
        background: `linear-gradient(135deg, rgba(144, 90, 1, 0.15) 0%, rgba(10, 10, 10, 0.95) 60%, rgba(163, 242, 47, 0.08) 100%)`,
        borderTop: `1px solid rgba(163, 242, 47, 0.18)`,
        borderBottom: `1px solid rgba(163, 242, 47, 0.18)`,
        py: { xs: 1.5, sm: 2.5 },
        px: { xs: 1.5, sm: 4, md: 8, lg: 16 },
      }}
    >
      {/* Section header */}
      {/* <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1.5}
        mb={{ xs: 1.5, sm: 2 }}
      >
        <Box
          sx={{
            width: 28,
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${SECONDARY}, ${PRIMARY})`,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            background: `linear-gradient(90deg, ${SECONDARY} 0%, white 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t?.metalsPriceTable || 'Gold & Silver Price Table — KD / gram'}
        </Typography>
        <Box
          sx={{
            width: 28,
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${PRIMARY}, ${SECONDARY})`,
          }}
        />
      </Box> */}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          background: 'rgba(17, 17, 17, 0.85)',
          border: `1px solid rgba(163, 242, 47, 0.15)`,
          borderRadius: 3,
          backdropFilter: 'blur(12px)',
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                background: `yellow`,
              }}
            >
              <TableCell
                align="center"
                sx={{
                  color: PRIMARY,
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  borderBottom: `1px solid rgba(163, 242, 47, 0.2)`,
                  py: { xs: 1, sm: 1.5 },
                  letterSpacing: '0.05em',
                }}
              >
                {t?.karat || 'Karat'}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  color: PRIMARY,
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  borderBottom: `1px solid rgba(163, 242, 47, 0.2)`,
                  py: { xs: 1, sm: 1.5 },
                  letterSpacing: '0.05em',
                }}
              >
                Live Gold Rate
              </TableCell>
              {/* <TableCell
                align="center"
                sx={{
                  color: PRIMARY,
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  borderBottom: `1px solid rgba(163, 242, 47, 0.2)`,
                  py: { xs: 1, sm: 1.5 },
                  letterSpacing: '0.05em',
                }}
              >
                Buying in KD
              </TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* ── Gold rows ── */}
            {karats.map(({ label, sellKey, altKey, type }, idx) => {
              const sellPrice = getSellPrice(sellKey, altKey, type);
              const buyPrice  = getBuyPrice(sellKey, altKey, type);
              const isEven = idx % 2 === 0;

              return (
                <TableRow
                  key={label}
                  sx={{
                    background: isEven
                      ? 'rgba(255, 255, 255, 0.01)'
                      : 'rgba(163, 242, 47, 0.03)',
                    '&:last-child td': { border: 0 },
                    transition: 'background 0.2s',
                    '&:hover': { background: 'rgba(163, 242, 47, 0.06)' },
                  }}
                >
                  {/* Karat label */}
                  <TableCell align="center" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', py: { xs: 0.8, sm: 1.2 } }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.3, sm: 0.5 },
                        borderRadius: 1.5,
                        background: `yellow`,
                        border: `1px solid rgba(163, 242, 47, 0.25)`,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.95rem' }, color: PRIMARY, fontFamily: 'monospace' }}>
                        {label}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Selling price – now green */}
                  <TableCell align="center" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', py: { xs: 0.8, sm: 1.2 } }}>
                    {<Box 
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.3, sm: 0.5 },
                        borderRadius: 1.5,
                        background: `yellow`,
                        border: `1px solid rgba(163, 242, 47, 0.25)`,
                      }}>
                        <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: { xs: '0.85rem', sm: '1rem' }, color: 'darkgreen', letterSpacing: '0.5px' }}>
                          {sellPrice !== null ? formatPrice(sellPrice) : '—'}
                        </Typography>
                        {/* <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, color: 'text.secondary' }}>KD</Typography> */}
                      </Box>}
                  </TableCell>

                  {/* Buying price – also green */}
                  
                </TableRow>
              );
            })}

            {/* ── Silver section divider ── */}
            {/* <TableRow sx={{ background: 'rgba(192,192,192,0.06)' }}>
              <TableCell
                colSpan={3}
                sx={{
                  py: 0.6,
                  borderBottom: '1px solid rgba(192,192,192,0.2)',
                  borderTop: '1px solid rgba(192,192,192,0.2)',
                }}
              >
                <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                  <Box sx={{ flex: 1, height: '1px', background: 'rgba(192,192,192,0.25)' }} />
                  <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, color: '#C0C0C0', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {(t?.silver).split(' ')[0] || 'Silver'} (KG)
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', background: 'rgba(192,192,192,0.25)' }} />
                </Box>
              </TableCell>
            </TableRow> */}

            {/* ── Silver row ── */}
            <TableRow
              sx={{
                background: 'rgba(192,192,192,0.03)',
                transition: 'background 0.2s',
                '&:hover': { background: 'rgba(192,192,192,0.07)' },
                '&:last-child td': { border: 0 },
              }}
            >
              {/* Metal label */}
              <TableCell align="center" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', py: { xs: 0.8, sm: 1.2 } }}>
                <Box
                  sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.3, sm: 0.5 },
                        borderRadius: 1.5,
                        background: `yellow`,
                        border: `1px solid rgba(163, 242, 47, 0.25)`,
                      }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.95rem' }, color: PRIMARY, fontFamily: 'monospace' }}>
                    {(t?.silver).split(' ')[0] || 'Ag'}
                  </Typography>
                </Box>
              </TableCell>

              {/* Silver selling price */}
              <TableCell align="center" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', py: { xs: 0.8, sm: 1.2 } }}>
                {<Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.3, sm: 0.5 },
                        borderRadius: 1.5,
                        background: `yellow`,
                        border: `1px solid rgba(163, 242, 47, 0.25)`,
                      }}>
                    <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: { xs: '0.85rem', sm: '1rem' }, color: 'darkgreen', letterSpacing: '0.5px' }}>
                      {metalRates?.silver != null ? formatPrice(metalRates.silver*1000) : '—'}
                    </Typography>
                  </Box>}
              </TableCell>

              {/* Silver buying price – no formula defined, show dash */}
              
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footnote */}
      {/* <Box display="flex" justifyContent="center" mt={1}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.6rem', sm: '0.68rem' }, textAlign: 'center', opacity: 0.7 }}
        >
          {t?.tablePriceNote || 'Buying price = Selling price minus deduction per karat  •  Prices in KD/gram'}
        </Typography>
      </Box> */}
    </Box>
  );
};

// API Service for fetching precious metals current rates
const fetchPreciousMetalsRates = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/rates`);
    const data = await response.json();

    if (data.success) {
      return {
        success: true,
        data: data.data,
        timestamp: data.timestamp
      };
    }
    throw new Error('API returned unsuccessful response');
  } catch (error) {
    console.error('Error fetching precious metals rates:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// API Service for fetching historical rates
const fetchHistoricalRates = async (metal = 'gold', limit = 50) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/historical-rates/${metal}?limit=${limit}`);
    const data = await response.json();

    if (data.success) {
      console.log(data.success);

      return {
        success: true,
        data: data.data
      };
    }
    throw new Error('API returned unsuccessful response');
  } catch (error) {
    console.error(`Error fetching historical ${metal} rates:`, error);
    return {
      success: false,
      error: error.message
    };
  }
};

// The API labels Kuwaiti dinar rates "KWD/..."; older payloads use "KD/...".
// Accept both, otherwise every point is filtered out and the chart is empty.
const PER_GRAM_UNITS = ["KWD/gram", "KD/gram"];
const PER_KG_UNITS = ["KWD/kg", "KD/kg"];

// Process historical data for charts
const processHistoricalData = (historicalData, metal) => {
  if (!historicalData || !Array.isArray(historicalData)) {
    return [];
  }

  const sortedData = metal.toLowerCase() === 'silver'
    ? historicalData
      .filter((a) => PER_KG_UNITS.includes(a.unit) || PER_GRAM_UNITS.includes(a.unit))
      .map((a) => (PER_KG_UNITS.includes(a.unit)
        ? {
          ...a,
          price: a.price / 1000,               // convert to per gram
          adjustedPrice: a.adjustedPrice / 1000,
          unit: "KD/gram"                     // update the unit label
        }
        : a))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    : historicalData
      .filter((a) => PER_GRAM_UNITS.includes(a.unit))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return sortedData.map(item => ({
    time: new Date(item.timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }),
    fullTime: new Date(item.timestamp).toLocaleString(),
    price: item.price,
    karat: item.karat,
    unit: item.unit
  }));
};

// Mobile-Optimized PriceCard Component
const PriceCard = ({ theme, formatPrice, t, formatChange, title, price, change, isMain = false }) => (
  <Grow in={true} timeout={500}>
    <Card sx={{
      mb: isMain ? 0 : 1,
      minHeight: { xs: 70, sm: isMain ? 100 : 80 }, // Reduced height on mobile
      display: 'flex',
      alignItems: 'center',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: { xs: 'none', sm: 'translateY(-2px)' }, // Disable hover transform on mobile
        boxShadow: { xs: 'none', sm: theme.shadows[8] }
      },
      border: isMain ? '1px solid rgba(163, 242, 47, 0.3)' : '1px solid rgba(255,255,255,0.08)',
      background: isMain
        ? 'linear-gradient(135deg, rgba(163, 242, 47, 0.06) 0%, rgba(144, 90, 1, 0.04) 100%)'
        : 'rgba(255,255,255,0.02)'
    }}>
      <CardContent sx={{
        py: { xs: 1, sm: isMain ? 2 : 1.5 }, // Reduced padding on mobile
        px: { xs: 1.5, sm: 2 }, // Reduced horizontal padding on mobile
        width: '100%',
        '&:last-child': { pb: { xs: 1, sm: isMain ? 2 : 1.5 } }
      }}>
        {<>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                variant={isMain ? { xs: "body1", sm: "h6" } : { xs: "body2", sm: "body2" }}
                color="text.secondary"
                sx={{
                  fontWeight: isMain ? 600 : 400,
                  fontSize: { xs: isMain ? '0.8rem' : '0.8rem', sm: 'inherit' }
                }}
              >
                {title}
              </Typography>
              <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
                <Box textAlign="right">
                  <Typography
                    variant={isMain ? { xs: "h6", sm: "h5" } : { xs: "body1", sm: "h6" }}
                    fontWeight="bold"
                    color="text.primary"
                    sx={{
                      fontFamily: 'monospace',
                      letterSpacing: '0.5px',
                      fontSize: { xs: isMain ? '0.9rem' : '0.8rem', sm: 'inherit' }
                    }}
                  >
                    {formatPrice(price)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.55rem' } }}
                  >
                    {t.kwd}
                  </Typography>
                </Box>
                {change !== undefined && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: change < 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                    borderRadius: 1,
                    px: { xs: 0.3, sm: 0.5 },
                    py: { xs: 0.15, sm: 0.25 }
                  }}>
                    {change < 0 ? (
                      <TrendingDown color="error" fontSize="small" />
                    ) : (
                      <TrendingUp color="success" fontSize="small" />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            {change !== undefined && (
              <Box display="flex" justifyContent="flex-end" mt={0.5}>
                <Chip
                  label={formatChange(change)}
                  size="small"
                  color={change < 0 ? "error" : "success"}
                  variant="outlined"
                  sx={{
                    height: { xs: 18, sm: 20 },
                    fontSize: { xs: '0.5rem', sm: '0.6rem' },
                    fontFamily: 'monospace'
                  }}
                />
              </Box>
            )}
          </>
        }
      </CardContent>
    </Card>
  </Grow>
);

// Mobile-Optimized CustomTooltip Component
const CustomTooltip = ({ formatPrice, metalRates, priceChanges, active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box sx={{
        bgcolor: 'rgba(42, 42, 42, 0.95)',
        border: '1px solid rgba(163, 242, 47, 0.3)',
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 }, // Reduced padding on mobile
        backdropFilter: 'blur(10px)',
        maxWidth: { xs: '200px', sm: 'none' } // Limit width on mobile
      }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
        >
          {data.fullTime}
        </Typography>
        <Typography
          variant={{ xs: "body1", sm: "h6" }}
          color="primary.main"
          sx={{
            fontFamily: 'monospace',
            fontSize: { xs: '0.9rem', sm: '1.25rem' }
          }}
        >
          {formatPrice(data.price)} KD
        </Typography>
        {data.karat && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
          >
            {data.karat.toUpperCase()} • {data.unit}
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};


const HomeScreen = () => {
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedMetal, setSelectedMetal] = useState('Gold');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  // API-driven states
  const [metalRates, setMetalRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [countdown, setCountdown] = useState(30);
  const [priceChanges, setPriceChanges] = useState({});
  const previousRatesRef = useRef(null);
  const t = translations[language];
  const theme = createAppTheme(true, language === 'ar');

  // Load initial data
  useEffect(() => {
    loadPreciousMetalsData();
    loadHistoricalData(selectedMetal);
  }, []);

  // Load historical data when metal selection changes
  useEffect(() => {
    loadHistoricalData(selectedMetal);
  }, [selectedMetal]);

  // Auto-refresh timer
  useEffect(() => {
    const timer = setInterval(() => {
      loadPreciousMetalsData();
      loadHistoricalData(selectedMetal);
    }, 60000);

    const countdownTimer = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 30);
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(countdownTimer);
    };
  }, [selectedMetal]);

  const loadPreciousMetalsData = async () => {
    setLoading(true);
    setError(null);

    const result = await fetchPreciousMetalsRates();

    if (result.success) {
      const processedData = processAPIData(result.data);

      const previousRates = previousRatesRef.current;
      const changes = calculatePriceChanges(processedData, previousRates);


      // Update state and ref
      setMetalRates(processedData);
      setPriceChanges(changes);
      setLastUpdated(new Date(result.timestamp));
      previousRatesRef.current = processedData; // Store for next comparison
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const loadHistoricalData = async (metal) => {
    setChartLoading(true);

    const result = await fetchHistoricalRates(metal, 50);

    if (result.success) {
      const processedHistory = processHistoricalData(result.data, metal);
      setPriceHistory(processedHistory);
    } else {
      console.error(`Failed to load ${metal} historical data:`, result.error);
      // Fallback to empty array or keep existing data
      setPriceHistory([]);
    }

    setChartLoading(false);
  };

  const processAPIData = (apiData) => {
    const processed = {
      gold: {},
      silver: 0,
      platinum: 0
    };

    if (Array.isArray(apiData)) {
      apiData.forEach(item => {
        if (item.metal === 'gold') {
          if (item.unit === 'USD/oz') {
            processed.gold['USD'] = item.price;
          } else {
            processed.gold[item.karat] = item.price;
          }
        } else if (item.metal === 'silver') {
          // Convert from KD/kg to KD/gram if needed
          processed.silver = item.unit === 'KD/kg' ? item.price / 1000 : item.price;
        } else if (item.metal === 'platinum') {
          processed.platinum = item.price;
        }
      });
    }

    return processed;
  };

  const calculatePriceChanges = (newData, oldData) => {
    if (!oldData) return {};

    const changes = {};

    // Calculate gold changes
    Object.keys(newData.gold).forEach(karat => {
      if (oldData.gold && oldData.gold[karat]) {
        changes[karat] = newData.gold[karat] - oldData.gold[karat];
      }
    });

    // Calculate silver and platinum changes
    if (oldData.silver) changes.silver = newData.silver - oldData.silver;
    if (oldData.platinum) changes.platinum = newData.platinum - oldData.platinum;

    return changes;
  };

  const formatPrice = (price) => {
    return price ? price.toFixed(3) : '0.000';
  };

  const formatChange = (change) => {
    if (!change) return '0.000';
    return `${change > 0 ? '+' : ''}${change.toFixed(3)}`;
  };

  const handleMetalSelection = (metal) => {
    setSelectedMetal(metal.toLowerCase());
  };

  // Mobile-Optimized TopPriceBar Component
  const TopPriceBar = () => {
    const mainGoldPrice = metalRates?.gold['USD'] || metalRates?.gold['USD'] || 0;
    const mainChange = priceChanges['USD'] || priceChanges['USD'] || 0;

    return (
      <Box sx={{
        bgcolor: 'linear-gradient(90deg, rgba(144, 90, 1, 0.2) 0%, rgba(163, 242, 47, 0.08) 100%)',
        p: { xs: 1, sm: 1.5 }, // Reduced padding on mobile
        borderBottom: '1px solid rgba(163, 242, 47, 0.2)',
        backdropFilter: 'blur(10px)',
        width: '100%'
      }}>
        <Box sx={{ px: { xs: 1.5, sm: 3 }, width: '100%' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{}}>
            <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 3 }} sx={{flex: 1,}} flexWrap="wrap">
              
                {<>
                    <Typography variant={{ xs: "h6", sm: "h5" }} sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontFamily: 'monospace',
                      letterSpacing: '1px',
                      fontSize: { xs: '1rem', sm: '1.5rem' }
                    }}>
                      ${formatPrice(mainGoldPrice)}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
                      {mainChange < 0 ? (
                        <TrendingDown sx={{ color: 'error.main', fontSize: { xs: '1rem', sm: '1.5rem' } }} />
                      ) : (
                        <TrendingUp sx={{ color: 'success.main', fontSize: { xs: '1rem', sm: '1.5rem' } }} />
                      )}
                      <Typography
                        variant={{ xs: "caption", sm: "body2" }}
                        color={mainChange < 0 ? "error.main" : "success.main"}
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: { xs: '0.7rem', sm: '0.875rem' }
                        }}
                      >
                        {formatChange(mainChange)}
                      </Typography>
                    </Box>
                  </>
                }
            </Box>
<Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: { xs: 'center', lg: 'flex-start' },
                            mr: { lg: 4 },
                            margin:'5px',
                            cursor: 'pointer',
                        }}
                        // onClick={() => handleNavigation({ key: 'home', path: '/' })}
                    >
                        <div style={{display:'flex',flexDirection:'column',}}>
                        <img
                            src={coloredLogo}
                            alt="Logo"
                            style={{
                                height: '100px', // Adjust height as needed
                                width: 'auto',
                                objectFit: 'contain', // Maintain aspect ratio
                            }}
                        />
    <p style={{
            padding:'0px',
            margin:'0px',
            fontSize:'12px',
                            }}>شركة مجوهرات النابي</p>
                        </div>
                    </Box>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} sx={{ mt: { xs: 1, sm: 0 },flex: 1,justifyContent:'center' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                >
                  {lastUpdated ? lastUpdated.toLocaleTimeString() : '--:--:--'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  loadPreciousMetalsData();
                  loadHistoricalData(selectedMetal);
                }}
                disabled={loading || chartLoading}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 }
                }}
              >
                {loading || chartLoading ? (
                  <CircularProgress size={16} color="primary" />
                ) : (
                  <Refresh fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // Mobile-Optimized Main Return JSX
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh', width: '100%' }}>


        {/* <AppHeader
          language={language}
          setLanguage={setLanguage}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          t={t}
        /> */}

        <MobileDrawer
          drawerOpen={drawerOpen}
          language={language}
          setDrawerOpen={setDrawerOpen}
          t={t}
        />

        <TopPriceBar />
        {/* ── Buying / Selling Price Table (below logo) ── */}
        <MetalsBuySellTable
          metalRates={metalRates}
          formatPrice={formatPrice}
          t={t}
        />

        {/* Mobile-Optimized Price Update Banner */}
        <Box sx={{
          bgcolor: 'linear-gradient(90deg, rgba(163, 242, 47, 0.12) 0%, rgba(144, 90, 1, 0.12) 100%)',
          py: { xs: 1.5, sm: 2 },
          textAlign: 'center',
          borderBottom: '1px solid rgba(163, 242, 47, 0.15)',
          width: '100%'
        }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, width: '100%' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={{ xs: 2, sm: 3 }}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Update color="primary" fontSize="small" />
                <Typography
                  variant="body2"
                  color="primary.light"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  {t.pricesUpdate?.replace('{seconds}', countdown) || `Next update in ${countdown}s`}
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{
                    py: 0,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    '.MuiAlert-message': { fontSize: { xs: '0.7rem', sm: '0.8rem' } }
                  }}
                >
                  API Error: Using cached data
                </Alert>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 }, width: '100%' }}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Left Sidebar - Prices */}
            <Grid item xs={12} md={3} lg={3}>
              <Stack spacing={{ xs: 2, sm: 3 }}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(163, 242, 47, 0.08) 0%, rgba(144, 90, 1, 0.05) 100%)',
                  border: '1px solid rgba(163, 242, 47, 0.2)'
                }}>
                  <CardContent sx={{ pb: 2, px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <ShowChart color="primary" />
                      <Typography
                        variant={{ xs: "body1", sm: "h6" }}
                        color="primary"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        {t.goldPrice}
                      </Typography>
                    </Box>
                    <Stack spacing={1.5}>
                      <PriceCard
                        theme={theme}
                        formatPrice={formatPrice}
                        t={t}
                        formatChange={formatChange}
                        title={t.karat24}
                        price={metalRates?.gold['24k'] || metalRates?.gold['24K']}
                        change={priceChanges['24k'] || priceChanges['24K']}
                        isMain={true}
                      />
                      <PriceCard
                        theme={theme}
                        formatPrice={formatPrice}
                        t={t}
                        formatChange={formatChange}
                        title={t.karat22}
                        price={metalRates?.gold['22k'] || metalRates?.gold['22K']}
                        change={priceChanges['22k'] || priceChanges['22K']}
                      />
                      <PriceCard
                        theme={theme}
                        formatPrice={formatPrice}
                        t={t}
                        formatChange={formatChange}
                        title={t.karat21}
                        price={metalRates?.gold['21k'] || metalRates?.gold['21K']}
                        change={priceChanges['21k'] || priceChanges['21K']}
                      />
                      <PriceCard
                        theme={theme}
                        formatPrice={formatPrice}
                        t={t}
                        formatChange={formatChange}
                        title={t.karat18}
                        price={metalRates?.gold['18k'] || metalRates?.gold['18K']}
                        change={priceChanges['18k'] || priceChanges['18K']}
                      />
                    </Stack>
                  </CardContent>
                </Card>

                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.08) 0%, rgba(192, 192, 192, 0.03) 100%)',
                  border: '1px solid rgba(192, 192, 192, 0.2)'
                }}>
                  <CardContent sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <ShowChart sx={{ color: '#C0C0C0' }} />
                      <Typography
                        variant={{ xs: "body1", sm: "h6" }}
                        sx={{
                          fontWeight: 700,
                          color: '#C0C0C0',
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        {t.silverPrice}
                      </Typography>
                    </Box>
                    <PriceCard
                      theme={theme}
                      formatPrice={formatPrice}
                      t={t}
                      formatChange={formatChange}
                      title={t.silver}
                      price={metalRates?.silver}
                      change={priceChanges.silver}
                      isMain={true}
                    />
                  </CardContent>
                </Card>

                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(229, 228, 226, 0.08) 0%, rgba(229, 228, 226, 0.03) 100%)',
                  border: '1px solid rgba(229, 228, 226, 0.2)'
                }}>
                  <CardContent sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <ShowChart sx={{ color: '#E5E4E2' }} />
                      <Typography
                        variant={{ xs: "body1", sm: "h6" }}
                        sx={{
                          fontWeight: 700,
                          color: '#E5E4E2',
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        {t.platinumPrice}
                      </Typography>
                    </Box>
                    <PriceCard
                      theme={theme}
                      formatPrice={formatPrice}
                      t={t}
                      formatChange={formatChange}
                      title={t.platinum}
                      price={metalRates?.platinum}
                      change={priceChanges.platinum}
                      isMain={true}
                    />
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Main Content - Chart */}
            <Grid item xs={12} md={9} lg={9} sx={{ width: '100%' }}>
              <Card sx={{
                height: { xs: 400, sm: 500, md: 650 }, // Responsive height
                background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(10px)'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    gap={{ xs: 2, sm: 0 }}
                  >
                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                      <Typography
                        variant={{ xs: "h6", sm: "h5" }}
                        sx={{
                          fontWeight: 700,
                          mb: 0.5,
                          fontSize: { xs: '1.1rem', sm: '1.5rem' }
                        }}
                      >
                        {t.realTimeChart}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {t.livePricing || 'Live precious metals pricing'}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={{ xs: 0.5, sm: 1 }} flexWrap="wrap" justifyContent="center">
                      <Button
                        variant={selectedMetal === 'Gold' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setSelectedMetal('Gold')}
                        sx={{
                          color: selectedMetal === 'Gold' ? 'black' : 'text.primary',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          minWidth: { xs: 60, sm: 80 },
                          fontSize: { xs: '0.7rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          py: { xs: 0.5, sm: 1 }
                        }}
                      >
                        {t.gold}
                      </Button>
                      <Button
                        variant={selectedMetal === 'Silver' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setSelectedMetal('Silver')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          minWidth: { xs: 60, sm: 80 },
                          fontSize: { xs: '0.7rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          py: { xs: 0.5, sm: 1 }
                        }}
                      >
                        {t.silver}
                      </Button>
                      <Button
                        variant={selectedMetal === 'Platinum' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setSelectedMetal('Platinum')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          minWidth: { xs: 60, sm: 80 },
                          fontSize: { xs: '0.7rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          py: { xs: 0.5, sm: 1 }
                        }}
                      >
                        {t.platinum}
                      </Button>
                    </Box>
                  </Box>

                  <Box height={{ xs: 280, sm: 350, md: 450 }} sx={{ mb: 3 }}>
                    {chartLoading ? (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <CircularProgress size={60} color="primary" />
                      </Box>
                    ) : priceHistory.length === 0 ? (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%" flexDirection="column">
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          mb={1}
                          sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            textAlign: 'center'
                          }}
                        >
                          {`${t.noHistoricalData || 'No historical data available for'} ${t[selectedMetal.toLowerCase()] || selectedMetal}`}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            textAlign: 'center'
                          }}
                        >
                          {t.tryRefreshing || 'Try refreshing or selecting a different metal'}
                        </Typography>

                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={priceHistory}>
                          <defs>
                            <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#A3F22F" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#A3F22F" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis
                            dataKey="time"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#b3b3b3', fontSize: { xs: 10, sm: 12 } }}
                          />
                          <YAxis
                            domain={['dataMin - 0.01', 'dataMax + 0.01']}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#b3b3b3', fontSize: { xs: 10, sm: 12 } }}
                            tickFormatter={(value) => value.toFixed(3)}
                          />
                          <Tooltip content={<CustomTooltip formatPrice={formatPrice} metalRates={metalRates} priceChanges={priceChanges} />} />
                          <Area
                            type="monotone"
                            dataKey="price"
                            stroke="#A3F22F"
                            strokeWidth={3}
                            fill="url(#colorGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Mobile-Optimized Floating Help Button */}
        {/* <Box sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          zIndex: 1000
        }}>
          <Button
            variant="contained"
            startIcon={<ContactMail />}
            sx={{
              bgcolor: 'primary.main',
              color: 'black',
              borderRadius: 3,
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              fontWeight: 700,
              boxShadow: theme.shadows[8],
              textTransform: 'none',
              transition: 'all 0.3s ease',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              '&:hover': {
                transform: { xs: 'none', sm: 'translateY(-2px)' },
                boxShadow: { xs: theme.shadows[8], sm: theme.shadows }
              }
            }}
            onClick={() => window.open('https://wa.me/96566111290', '_blank')}
          >
            {t.help}
          </Button>
        </Box> */}
        <AppFooter t={t} />
      </Box>
    </ThemeProvider>
  );
};

export default HomeScreen;